import React, { useCallback, useEffect } from 'react';
import {
    useBottomImageIndex,
    useIsMeetingCornerSettingLoadedFromDB,
    useTopImageIndex,
    useWidgetsList
} from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import jwtDecode from 'jwt-decode';
import { useEvent } from 'kits/eventKit';
import { useNavigate } from 'react-router';
import { idpService } from 'services/api/identity-provider.service';
import { getTokens } from 'services/api/token-storage.service';
import { useHiddenDashboardWidgets } from 'shared-state/directory/hooks';
import { useIdentityClaims } from 'shared-state/identity/hooks';
import { IIdentityClaims } from 'shared-state/identity/types';
import { useCurrentOrgId, useCurrentRoomId } from 'shared-state/location/hook';

export const IdentityAdapter: React.FC<{}> = () => {
    const [, setIdentityClaims] = useIdentityClaims();
    const [, setCurrentOrgId] = useCurrentOrgId();
    const [, setTopImageIndex] = useTopImageIndex();
    const [, setBottomImageIndex] = useBottomImageIndex();
    const [, setWidgetsList] = useWidgetsList();
    const [, setIsMeetingCornerSettingLoadedFromDB] = useIsMeetingCornerSettingLoadedFromDB();
    const [, setHiddenDashboardWidgets] = useHiddenDashboardWidgets();
    const [currentRoomId] = useCurrentRoomId();
    const routeHistory = useNavigate();

    const parseToken = () => {
        const tokens = getTokens();

        if (!tokens.accessToken) {
            routeHistory('/login');
            return;
        }
        const accessTokenClaims = jwtDecode<any>(tokens.accessToken);
        if (!accessTokenClaims || !accessTokenClaims.sub) return;

        const userSub: string = accessTokenClaims.sub;

        // Backwards comp
        let type = accessTokenClaims.role;
        const uid = userSub.split(':')[2];
        const upn = accessTokenClaims.email;
        const areas = ['*'];
        const displayName = accessTokenClaims.name;
        const idp = userSub.split(':')[0];
        let roles = [accessTokenClaims.role];
        const tid = userSub.split(':')[1];
        const profileImageURL = accessTokenClaims.profile_pic;
        const msTenantId = accessTokenClaims.msTenantId;
        const visitorInvId = accessTokenClaims.invitationId; // for visitors
        const orgStatus = accessTokenClaims.org_status;
        setCurrentOrgId(tid);

        if (tokens.confirmationToken) {
            const confirmationTokenClaims = jwtDecode<any>(tokens.confirmationToken);
            type = confirmationTokenClaims.role;
            roles = [confirmationTokenClaims.role];
        }

        const userIdentity: IIdentityClaims = {
            type,
            uid,
            upn,
            areas,
            displayName,
            idp,
            roles,
            tid,
            profileImageURL,
            msTenantId,
            visitorInvId,
            orgStatus
        };

        setIdentityClaims(userIdentity);
    };

    const getProfileFromIdp = async () => {
        try {
            const userProfile = await idpService.getUser();
            setIdentityClaims((prevClaims: any) => {
                if (!prevClaims) return prevClaims;

                return {
                    ...prevClaims,
                    ...{
                        profileImageURL: userProfile.profile_pic || undefined,
                        displayName: userProfile.name
                    }
                };
            });
            setHiddenDashboardWidgets(userProfile.hidden_dashboard_widgets ?? []);
        } catch (error) {
            console.log(error);
        }
    };

    const updatePersonalMeetingCornerSettings = useCallback(
        async (roomId: string) => {
            if (!roomId?.includes('personal-')) return;

            try {
                const userProfile = await idpService.getUser();
                const isOwnRoom = roomId.includes(userProfile.uid);

                if (isOwnRoom) {
                    const { top_image_index, bottom_image_index, widgets_list } = userProfile.personal_meeting_corner;
                    setTopImageIndex(top_image_index);
                    setBottomImageIndex(bottom_image_index);
                    setWidgetsList(widgets_list);
                } else {
                    const userId = roomId.split('personal-')[1]?.split('_')[0];
                    if (!userId || userId === 'undefined') return;

                    const { uid, personal_meeting_corner } = await idpService.getUserPersonalMeetingCornerSettings(userId);

                    if (roomId.includes(uid) && roomId === personal_meeting_corner.room_id) {
                        const { top_image_index, bottom_image_index, widgets_list } = personal_meeting_corner;
                        setTopImageIndex(top_image_index);
                        setBottomImageIndex(bottom_image_index);
                        setWidgetsList(widgets_list);
                    }
                }
                setIsMeetingCornerSettingLoadedFromDB(true);
            } catch (err) {
                console.error('Failed to load Personal Meeting Corner settings:', err);
            }
        },
        [setTopImageIndex, setBottomImageIndex, setWidgetsList]
    );

    const refreshIdentity = () => {
        parseToken();
        getProfileFromIdp();
    };

    useEffect(() => {
        refreshIdentity();
        updatePersonalMeetingCornerSettings(currentRoomId);
    }, []);

    useEffect(() => {
        updatePersonalMeetingCornerSettings(currentRoomId);
    }, [currentRoomId, updatePersonalMeetingCornerSettings]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    useEvent('refetchUserDetails', (data: any) => {
        refreshIdentity();
    });

    return null;
};
